import { useEffect, useRef, useState } from "react";
import type { GraphyClient, Scope } from "../api/client";
import { ApiError } from "../api/client";
import type { NeighborhoodResult, SourceExcerpt } from "../api/types";
import { ErrorState, Loading } from "./states";
import { T } from "../i18n";

// GY-038 source drawer: highlighted vulnerable lines + adjacent context, with
// repository/file/line/commit provenance, upstream (callers) / downstream
// (dependencies) links, and an entry point back into the explorer. Accessible:
// role=dialog + aria-modal, Escape closes, focus moves into the drawer on open
// and is RESTORED to the triggering element on close.

interface Props {
  client: GraphyClient;
  scope: Scope;
  symbolId: string;
  labelOf: (id: string) => string;
  onClose: () => void;
  onNavigate: (id: string) => void; // drill into explorer (finding/explorer entry point)
  // Only a symbol with a confirmed finding has "vulnerable" lines; otherwise the
  // highlighted line is just where the symbol is declared.
  hasFinding?: boolean;
  // Caller-provided relations (e.g. type-level dependencies on the system map)
  // instead of the method-level neighbourhood.
  related?: { upstreamTitle: string; downstreamTitle: string; upstream: string[]; downstream: string[] };
}

export function SourceDrawer({ client, scope, symbolId, labelOf, onClose, onNavigate, hasFinding = true, related }: Props) {
  const [excerpt, setExcerpt] = useState<SourceExcerpt | null>(null);
  const [neighbors, setNeighbors] = useState<NeighborhoodResult | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [attempt, setAttempt] = useState(0);
  const panelRef = useRef<HTMLDivElement>(null);
  const restoreFocusRef = useRef<HTMLElement | null>(null);

  // Capture the element to return focus to, then move focus into the drawer.
  useEffect(() => {
    restoreFocusRef.current = document.activeElement as HTMLElement | null;
    panelRef.current?.focus();
    return () => {
      restoreFocusRef.current?.focus?.();
    };
  }, []);

  useEffect(() => {
    let live = true;
    setLoading(true);
    setError(false);
    (async () => {
      // Settle independently: a symbol whose source was not indexed (404) is an
      // expected, honest absence and must not hide its callers/dependencies.
      const [src, nb] = await Promise.allSettled([
        client.source(scope, symbolId, 20),
        related ? Promise.resolve(null) : client.neighborhood({ ...scope, origin: symbolId, direction: "both", depth: 1 }),
      ]);
      if (!live) return;
      const notFound = (r: PromiseSettledResult<unknown>) =>
        r.status === "rejected" && r.reason instanceof ApiError && r.reason.code === "not_found";
      setExcerpt(src.status === "fulfilled" ? src.value : null);
      setNeighbors(nb.status === "fulfilled" ? (nb.value as NeighborhoodResult | null) : null);
      // Only a real failure (network, 5xx, auth) is an error; "not found" is not.
      const failed = [src, nb].some((r) => r.status === "rejected" && !notFound(r));
      setError(failed);
      setLoading(false);
    })();
    return () => {
      live = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [symbolId, scope.snapshotId, attempt]);

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Escape") {
      e.stopPropagation();
      onClose();
    }
  };

  // Upstream = callers (predecessors); downstream = dependencies (successors).
  const upstream = related?.upstream ?? (neighbors?.edges ?? []).filter(([, t]) => t === symbolId).map(([s]) => s);
  const downstream = related?.downstream ?? (neighbors?.edges ?? []).filter(([s]) => s === symbolId).map(([, t]) => t);

  return (
    <div className="drawer-scrim" onClick={onClose}>
      <div
        className="drawer"
        role="dialog"
        aria-modal="true"
        aria-label={T.source.title}
        tabIndex={-1}
        ref={panelRef}
        onKeyDown={onKeyDown}
        onClick={(e) => e.stopPropagation()}
      >
        <header className="drawer-header">
          <div>
            <strong>{labelOf(symbolId)}</strong>
            {excerpt && (
              <div className="muted drawer-loc">
                {excerpt.repo} · {excerpt.path}:{excerpt.vulnStart}
                {excerpt.vulnEnd !== excerpt.vulnStart ? `-${excerpt.vulnEnd}` : ""} · {excerpt.commit.slice(0, 10)}
              </div>
            )}
          </div>
          <button className="btn" onClick={onClose} aria-label={T.source.close}>
            {T.source.close}
          </button>
        </header>

        {loading ? (
          <Loading />
        ) : error ? (
          <ErrorState onRetry={() => setAttempt((a) => a + 1)} />
        ) : (
          <div className="drawer-body">
            {excerpt ? (
              <>
                {excerpt.truncated && <div className="drawer-note">{hasFinding ? T.source.truncated : `Showing lines ${excerpt.startLine}–${excerpt.endLine} around the declaration.`}</div>}
                <pre className={`code lang-${excerpt.language ?? "text"}`} aria-label={`${excerpt.path} source excerpt`}>
                  {excerpt.lines.map((l) => (
                    <div key={l.number} className={l.vulnerable ? (hasFinding ? "code-line vuln" : "code-line focus") : "code-line"}>
                      <span className="code-gutter">{l.number}</span>
                      <code>{l.text}</code>
                    </div>
                  ))}
                </pre>
              </>
            ) : (
              <div className="drawer-empty">
                <strong>Source isn't indexed for this symbol</strong>
                <span className="muted">This snapshot has no source for it, so only its relationships are shown.</span>
              </div>
            )}

            <div className="drawer-links">
              <section>
                <h4>{related?.upstreamTitle ?? T.source.upstream}</h4>
                <LinkList ids={upstream} labelOf={labelOf} onNavigate={onNavigate} />
              </section>
              <section>
                <h4>{related?.downstreamTitle ?? T.source.downstream}</h4>
                <LinkList ids={downstream} labelOf={labelOf} onNavigate={onNavigate} />
              </section>
            </div>

            {!related && (
              <button className="btn" onClick={() => onNavigate(symbolId)}>
                {T.source.openInExplorer}
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function LinkList({ ids, labelOf, onNavigate }: { ids: string[]; labelOf: (id: string) => string; onNavigate: (id: string) => void }) {
  if (ids.length === 0) return <p className="muted">{T.source.none}</p>;
  return (
    <ul className="node-list">
      {ids.map((id) => (
        <li key={id}>
          <button className="link" onClick={() => onNavigate(id)}>
            {labelOf(id)}
          </button>
        </li>
      ))}
    </ul>
  );
}
